import React from "react";
import { useLocale } from "../../i18n";
import SEO, { buildBreadcrumb } from "../../components/SEO";

/**
 * LegalShell — shared editorial layout for Privacy / Terms / Cookies.
 * Preserves the site typography and rhythm: mono kicker → editorial H1 → serif+sans body.
 *
 * Props:
 *   kicker          — "Trust · Privacy" style small mono label
 *   title           — H1 text
 *   updated         — string like "February 15, 2026" for the last-updated line
 *   description     — SEO meta description (~155 chars)
 *   path            — canonical path (e.g. "/privacy")
 *   sections        — array of { heading, body: React|string, id? }
 */
export default function LegalShell({ kicker, title, updated, description, path, sections = [] }) {
  const { locale } = useLocale();

  const breadcrumbLd = buildBreadcrumb([
    { name: locale === "ro" ? "Acasă" : "Home", path: "/" },
    { name: title, path },
  ]);

  return (
    <>
      <SEO
        title={title}
        description={description}
        path={path}
        jsonLd={[breadcrumbLd]}
      />
      <article className="max-w-[900px] mx-auto px-6 lg:px-10 py-16 lg:py-24" data-testid={`legal-${path.replace(/\//g, "")}`}>
        <p className="font-mono-wr text-[11px] uppercase tracking-[0.3em] text-[var(--wr-red)] mb-4">
          / {kicker}
        </p>
        <h1 className="font-editorial text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight text-[var(--wr-ink)] mb-6">
          {title}
        </h1>
        <p className="font-mono-wr text-[11px] uppercase tracking-widest text-[var(--wr-ink-2)] mb-16" data-testid="legal-updated">
          {locale === "ro" ? "Actualizat" : "Last updated"} · {updated}
        </p>

        <div className="space-y-14 text-[16px] leading-[1.7] text-[var(--wr-ink)]">
          {sections.map((s, i) => (
            <section key={i} id={s.id} data-testid={`legal-section-${i}`}>
              <h2 className="font-editorial text-3xl sm:text-4xl leading-tight tracking-tight text-[var(--wr-ink)] mb-5">
                {s.heading}
              </h2>
              <div className="space-y-4">{s.body}</div>
            </section>
          ))}
        </div>
      </article>
    </>
  );
}
